import type { SocialMediaPlatformDto } from '@inference.engine/persona-api-client/web';
import LinkedInIcon from '@/shared/assets/images/linkedin.svg';
import TwitterIcon from '@/shared/assets/images/twitter.svg';
import InstagramIcon from '@/shared/assets/images/instagram.svg';
import TikTokIcon from '@/shared/assets/images/tiktok.svg';
import FacebookIcon from '@/shared/assets/images/facebook.svg';
import YouTubeIcon from '@/shared/assets/images/youtube.svg';
import PinterestIcon from '@/shared/assets/images/pinterest.svg';
import WhatsappIcon from '@/shared/assets/images/whatsapp_logo.svg';
import DiscordIcon from '@/shared/assets/images/discord_logo.svg';
import ThreadsIcon from '@/shared/assets/images/threads_logo.svg';
import SnapchatIcon from '@/shared/assets/images/snapchat_logo.svg';
import TwitchIcon from '@/shared/assets/images/twitch_logo.svg';
import RedditIcon from '@/shared/assets/images/reddit_logo.svg';
import TelegramIcon from '@/shared/assets/images/telegram_logo.svg';
import LinkIcon from '@/shared/assets/images/link.svg';
import { i18n } from '@/shared/i18n';

export type SocialMediaPlatformName = SocialMediaPlatformDto['platformName'];

export interface SocialMediaPlatformConfig {
  color: string;
  image: string;
  url: string;
  description: string;
}

// Getters, not values: this map is built while the module is evaluated, so resolving per
// read is what lets a description follow a language change instead of freezing at import.
export const SOCIAL_MEDIA_PLATFORM_CONFIG: Record<
  SocialMediaPlatformName,
  SocialMediaPlatformConfig
> = {
  LinkedIn: {
    color: 'rgba(0, 119, 181, 1)',
    image: LinkedInIcon,
    url: 'https://www.linkedin.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.linkedIn');
    },
  },
  'Twitter/X': {
    color: 'rgba(0, 0, 0, 1)',
    image: TwitterIcon,
    url: 'https://www.twitter.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.twitterX');
    },
  },
  Instagram: {
    color: 'rgba(225, 48, 108, 1)',
    image: InstagramIcon,
    url: 'https://www.instagram.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.instagram');
    },
  },
  TikTok: {
    color: 'rgba(42, 240, 234, 1)',
    image: TikTokIcon,
    url: 'https://www.tiktok.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.tikTok');
    },
  },
  Facebook: {
    color: 'rgba(24, 119, 242, 1)',
    image: FacebookIcon,
    url: 'https://www.facebook.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.facebook');
    },
  },
  YouTube: {
    color: 'rgba(255, 0, 0, 1)',
    image: YouTubeIcon,
    url: 'https://www.youtube.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.youTube');
    },
  },
  Pinterest: {
    color: 'rgba(189, 8, 28, 1)',
    image: PinterestIcon,
    url: 'https://www.pinterest.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.pinterest');
    },
  },
  Threads: {
    color: 'rgb(7, 31, 40)',
    image: ThreadsIcon,
    url: 'https://www.threads.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.threads');
    },
  },
  WhatsApp: {
    color: 'rgba(37, 211, 102, 1)',
    image: WhatsappIcon,
    url: 'https://www.whatsapp.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.whatsApp');
    },
  },
  Discord: {
    color: 'rgba(88, 101, 242, 1)',
    image: DiscordIcon,
    url: 'https://www.discord.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.discord');
    },
  },
  Snapchat: {
    color: 'rgba(255, 252, 0, 1)',
    image: SnapchatIcon,
    url: 'https://www.snapchat.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.snapchat');
    },
  },
  Twitch: {
    color: 'rgba(145, 70, 255, 1)',
    image: TwitchIcon,
    url: 'https://www.twitch.tv',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.twitch');
    },
  },
  Reddit: {
    color: 'rgba(255, 69, 0, 1)',
    image: RedditIcon,
    url: 'https://www.reddit.com',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.reddit');
    },
  },
  Telegram: {
    color: 'rgba(0, 136, 204, 1)',
    image: TelegramIcon,
    url: 'https://www.telegram.org',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.telegram');
    },
  },
  Other: {
    color: 'rgba(136, 136, 136, 1)',
    image: LinkIcon,
    url: '',
    get description() {
      return i18n.t('common:constants.socialMediaPlatformsConstants.other');
    },
  },
};

export const getPlatformConfig = (platformName: string): SocialMediaPlatformConfig | undefined => {
  // Create a normalized lookup map (cache this if performance becomes an issue)
  const normalizedMap = new Map(
    Object.entries(SOCIAL_MEDIA_PLATFORM_CONFIG).map(([key, value]) => [
      key.toLowerCase().replace(/[^a-z0-9]/g, ''), // Remove special chars
      value,
    ]),
  );

  const normalizedInput = platformName.toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalizedMap.get(normalizedInput);
};
